/*** IMPORTS FROM imports-loader ***/
(function() {

module.exports = (function(
  YT
) {
  'use strict';
  const uuid = require('node-uuid').v4;
  const moment = require('moment');
  let _googleApiKeys = new WeakMap();
  let _videoIds = new WeakMap();
  let _videoSets = new WeakMap();
  let _containerElements = new WeakMap();
  let _videoSetParentObject = new WeakMap();
  let _contentDetailsPromises = new WeakMap();
  let _ytPlayers = new WeakMap();
  let _lagTolerance = new WeakMap();
  let _duration = new WeakMap();
  let _currentTime = new WeakMap();
  let _videoIndices = new WeakMap();

  /**
  * Set of youtube videos
  */
  class YouTubeVideoSet
  {

    /**
    * @param {YouTubeMultiVideoSync} parentObject parent controllery wotsit
    * @param {string[]} videoIds an array of video ids
    */
    constructor(parentObject, videoIds) {
      _videoSetParentObject.set(this, parentObject);
      _videoIds.set(this, videoIds);
      _contentDetailsPromises.set(
        this,
        Promise.all(
          this.videoIds.map(
            (id) => {
              return fetch(
                'https://www.googleapis.com/youtube/v3/videos?' +
                [
                  'part=contentDetails',
                  ('id=' + encodeURIComponent(id)),
                  (
                    'key=' +
                    encodeURIComponent(_googleApiKeys.get(parentObject))
                  ),
                ].join('&')
              ).then(
                (resp) => {
                  return resp.json();
                }
              ).then(
                (json) => {
                  return json.items[0].contentDetails;
                }
              );
            }
          )
        )
      );
      this.duration.then((durations) => {
        let uniqueDurations = [];
        durations.forEach((duration) => {
          if (uniqueDurations.indexOf(duration) === -1) {
            uniqueDurations.push(duration);
          }
        });
        if (uniqueDurations.length !== 1) {
          throw new Error('Durations do not match!');
        } else {
          _duration.set(parentObject, uniqueDurations[0]);
        }
      });
    }

    /**
    * an array of video ids
    * @return {string[]}
    */
    get videoIds() {
      return _videoIds.get(this).slice();
    }


    /**
    * The promise for retrieving contentDetails for all the videos specified.
    * @return {Promise}
    */
    get contentDetails() {
      return _contentDetailsPromises.get(this);
    }

    /**
    * Get the duration of all the videos as milliseconds
    * @return {int[]}
    */
    get duration() {
      return this.contentDetails.then((videos) => {
        return videos.map((contentDetails) => {
          return moment.duration(contentDetails.duration).asMilliseconds();
        });
      });
    }

    /**
    * Get the containers for the youtube iframes
    * @return {HTMLDivElement[]}
    */
    get containers() {
      if (_containerElements.has(this) === false) {
        _containerElements.set(
          this,
          this.videoIds.map((id) => {
            let element = document.createElement('div');
            element.id = ('id-' + uuid());
            return element;
          })
        );
      }
      return _containerElements.get(this).slice();
    }

    /**
    * Initialises youtube player api objects & sets up video syncing
    * @return {Promise}
    */
    get Player() {
      if (!_ytPlayers.has(this)) {
        _ytPlayers.set(
          this,
          this.contentDetails.then((videos) => {
            let videoIds = this.videoIds;
            let makeResync = (
              (target) => {
                let resync = () => {
                  this.Player.then(
                    (players) => {
                      requestAnimationFrame(resync);
                      players.forEach(
                        (player, i) => {
                          let iframe = player.getIframe();
                          let parentObject = _videoSetParentObject.get(this);
                          if (iframe) {
                            iframe.classList[
                              (
                                i === parentObject.videoIndex
                                  ? 'remove'
                                  : 'add'
                              )
                            ]('signpostmarv-multi-video-sync--video-hidden');
                          }
                          if (i > 0) {
                            let targetCurrentTime = target.getCurrentTime();
                            let playerCurrentTime = player.getCurrentTime();
                            let lagTolerance = _lagTolerance.get(parentObject);
                            _currentTime.set(parentObject, targetCurrentTime);
                            switch (target.getPlayerState()) {
                              case -1:
                              case 5:
                                player.stopVideo();
                              break;
                              case 0:
                                player.stopVideo();
                              break;
                              case 1:
                                if (
                                  (
                                    targetCurrentTime -
                                    playerCurrentTime
                                  ) > lagTolerance
                                ) {
                                  if (player.getPlayerState() === 3) {
                                    player.seekTo(targetCurrentTime, false);
                                  }
                                  player.seekTo(targetCurrentTime, true);
                                }

                                if (
                                  player.getPlayerState() !== 1 &&
                                  player.getPlayerState() !== 3
                                ) {
                                  player.playVideo();
                                }
                              break;
                              case 2:
                              case 3:
                                player.playVideo();
                                player.seekTo(targetCurrentTime, true);
                              break;
                            }
                          }
                        }
                      );
                    }
                  );
                };
                return resync;
              }
            );
            return videos.map((contentDetails, i) => {
              let player = new YT.Player(
                this.containers[i],
                {
                  width: 640,
                  height: 360,
                  videoId: videoIds[i],
                  playerVars: {
                    autoplay: 0,
                    controls: i > 0 ? 0 : 2,
                    disablekb: i > 0 ? 1 : 0,
                    modestbranding: 1,
                    origin: location.hostname,
                    playsinline: 1,
                    rel: 0,
                    showinfo: 0,
                  },
                  events: {
                    onReady: function(e) {
                      if (i > 0) {
                        e.target.mute();
                      } else {
                        makeResync(e.target)();
                      }
                    },
                  },
                }
              );
              return player;
            });
          })
        );
      }
      return _ytPlayers.get(this);
    }
  }

  /**
  * Controllery wotsit
  */
  class YouTubeMultiVideoSync
  {

    /**
    * @param {string} googleApiKey required for API interaction
    * @param {string[]} videoIds an array of video ids
    */
    constructor(googleApiKey, videoIds) {
      _googleApiKeys.set(this, googleApiKey);
      _lagTolerance.set(this, 0.0625 * 2);
      _videoSets.set(this, new YouTubeVideoSet(this, videoIds));
      this.videoIndex = 0;
    }

    /**
    * @return {YouTubeVideoSet}
    */
    get videoSet() {
      return _videoSets.get(this);
    }

    /**
    * Get the lag tolerance
    * @return {float}
    */
    get lagTolerance() {
      return _lagTolerance.get(this);
    }

    /**
    * Set the lag tolerance.
    * Setting this below the requestAnimationFrame interval is likely pointless
    * @param {float} tolerance
    */
    set lagTolerance(tolerance) {
      _lagTolerance.set(this, tolerance);
    }

    /**
    * Get the duration of the video set
    * @see {YouTubeVideoSet.constructor}
    * @return {float}
    */
    get duration() {
      return _duration.get(this);
    }

    /**
    * Get the current time of the control video
    * @see {YouTubeVideoSet.Player}
    * @return {float}
    */
    get currentTime() {
      return _currentTime.get(this);
    }

    /**
    * an array of video ids
    * @see {YouTubeVideoSet.videoIds}
    * @return {string[]}
    */
    get videoIds() {
      return this.videoSet.videoIds;
    }

    /**
    * Get the currently displayed video index
    * @todo move to a UI controllery wotsit
    * @return {int}
    */
    get videoIndex() {
      return _videoIndices.get(this);
    }

    /**
    * Set the current displayed video index
    * @todo move to a UI controllery wotsit
    * @param {int} index
    */
    set videoIndex(index) {
      return _videoIndices.set(this, index);
    }

    /**
    * Append the {YouTubeVideoSet.containers} elements to parentElement
    * @param {HtmlElement} parentElement
    */
    appendTo(parentElement) {
      this.videoSet.containers.map(
        (element) => {
          parentElement.appendChild(element);
        }
      );
      this.videoSet.Player;
    }

    /**
    * Set the control video away playing
    * {YouTubeVideoSet.Player} synchronises the status of the other videos
    */
    playVideo() {
      this.videoSet.Player.then(
        (players) => {
          players[0].playVideo();
        }
      );
    }

    /**
    * Set the control video to pause
    * {YouTubeVideoSet.Player} synchronises the status of the other videos
    */
    pauseVideo() {
      this.videoSet.Player.then(
        (players) => {
          players[0].pauseVideo();
        }
      );
    }

    /**
    * Set the control video to stop playback
    * {YouTubeVideoSet.Player} synchronises the status of the other videos
    */
    stopVideo() {
      this.videoSet.Player.then(
        (players) => {
          players[0].stopVideo();
        }
      );
    }

    /**
    * Set the control video to a specific point in the timeline
    * {YouTubeVideoSet.Player} synchronises the status of the other videos
    * @param {float} seconds
    * @param {bool} allowSeekAhead
    */
    seekTo(seconds, allowSeekAhead=false) {
      this.videoSet.Player.then(
        (players) => {
          players[0].seekTo(seconds, allowSeekAhead);
        }
      );
    }

    /**
    * Set the quality of the playback
    * @param {string} suggestedQuality
    */
    setPlaybackQuality(suggestedQuality) {
      this.videoSet.Player.then(
        (players) => {
          players.map(
            (player) => {
              player.setPlaybackQuality(suggestedQuality);
            }
          );
        }
      );
    }
  }
  return YouTubeMultiVideoSync;
})(
  this.YT,
  this.moment
);

}.call(window));


//////////////////
// WEBPACK FOOTER
// ./src/youtube.js
// module id = 1
// module chunks = 0